import {Injectable} from '@angular/core';
import {Tab} from '../_models/tab';
import {OrderItemCredit} from '../_models/order-item-credit';
import {TabCredit} from '../_models/tab-credit';
import {OrderItem} from '../_models/order-item';
import {Membership} from '../_models/membership';
import {ModifiedTabComponents} from '../_models/modified-tab-components';
import {OrderStatuses} from '../_constants/order-statuses';
import {addCurrency, divideCurrency, multiplyCurrency, subtractCurrency} from '../_utils/currency-math';
import {Order} from '../_models/order';
import {Tax} from '../_models/tax';
import {calculateTax, convertPercentage} from '../_utils/tax-utils';
import {OrganizationEvent} from '../_models/organization-event';
import {TabNote} from '../_models/tab-note';
import {CompDefinition} from '../_models/comp-definition';
import {Organization} from '../_models/organization';
import {OrganizationService} from './organization.service';
import {CompDefinitionTypes} from '../_constants/comp-definition-types';
import {TabPaymentInformation} from '../_models/tab-payment-information';

@Injectable({
    providedIn: 'root'
})
export class TabCalculationService {
    organization: Organization;
    autoComps: CompDefinition[];

    constructor(
        private organizationService: OrganizationService
    ) {
        this.organizationService.current.subscribe(o => {
            this.organization = !!o ? new Organization(o) : null;
            this.autoComps = !!this.organization
                ? this.organization.compDefinitions.filter(c => c.autoApply)
                : [];
        });
    }

    recalculate(t: Tab): Tab {
        const tab = new Tab(t);
        const initialRemaining = !!tab.paymentInformation && tab.paymentInformation.remaining;

        if (!tab.paymentInformation) {
            tab.paymentInformation = new TabPaymentInformation();
        }

        tab.paymentInformation.depositsRemaining = tab.orders.filter(o => o.status !== OrderStatuses.CANCELED)
            .reduce((oTotal, order) => addCurrency(oTotal, order.items
                .reduce((total, i) => addCurrency(total, i.calcDeposits()), 0)), 0);

        tab.paymentInformation.tokensUsed = tab.orders.filter(o => o.status !== OrderStatuses.CANCELED)
            .reduce((oTotal, order) => addCurrency(oTotal, order.items
                .reduce((total, i) => addCurrency(total, i.tokenPayment), 0)), 0);

        tab.paymentInformation.tokenPaymentsTotal = tab.orders.filter(o => o.status !== OrderStatuses.CANCELED)
            .reduce((oTotal, order) => addCurrency(oTotal, order.items
                .reduce((total, i) => addCurrency(total, i.tokenPayment > 0 ? i.total : 0), 0)), 0);

        tab.paymentInformation.giftCardPurchasesTotal = tab.orders.filter(o => o.status !== OrderStatuses.CANCELED)
            .reduce((items, order) => items.concat(order.items), [])
            .filter(i => !!i.giftCard).reduce((gTotal, i) => addCurrency(i.total, gTotal), 0);

        tab.paymentInformation.membershipRenewalsTotal = tab.orders.filter(o => o.status !== OrderStatuses.CANCELED)
            .reduce((items, order) => items.concat(order.items), [])
            .filter(i => !!i.membershipRenewal).reduce((mTotal, i) => addCurrency(i.total, mTotal), 0);

        this.applyComps(tab);
        this.groupTaxedAmounts(tab);

        const includedMembershipFee = tab.orders.reduce((membershipFee, order) => addCurrency(membershipFee,
            order.items.reduce((itemFee, i) => i.membershipFeeIncluded ? addCurrency(itemFee, i.membershipFees) : itemFee, 0)), 0);

        tab.subtotal = addCurrency(includedMembershipFee, tab.orders.filter(o => o.status === OrderStatuses.PENDING
            || o.status === OrderStatuses.RECEIVED
            || o.status === OrderStatuses.PREPARING
            || o.status === OrderStatuses.READY
            || o.status === OrderStatuses.DELIVERED)
            .reduce((orderTotal, order) => addCurrency(orderTotal, order.calculateTotal()), 0));

        tab.paymentInformation.subtotal = tab.subtotal;

        tab.invoiced = subtractCurrency(addCurrency(tab.addedTaxes,
            addCurrency(tab.totalFee, tab.subtotal)), tab.paymentInformation.credits);

        tab.membershipFee = tab.orders.reduce((membershipFee, order) => addCurrency(membershipFee,
            order.items.reduce((itemFee, i) => !i.membershipFeeIncluded ? addCurrency(itemFee, i.membershipFees) : itemFee, 0)), 0);

        tab.paymentInformation.taxesAndFees = addCurrency(tab.paymentInformation.taxes,
            addCurrency(
                !tab.totalFee
                    ? tab.membershipFee
                    : 0,
                tab.paymentInformation.surcharges));

        tab.paymentInformation.total = addCurrency(
            addCurrency(tab.paymentInformation.taxesAndFees, tab.invoiced),
            tab.donation);

        tab.paymentInformation.remaining = addCurrency(addCurrency(subtractCurrency(addCurrency(!tab.totalFee
                ? tab.membershipFee
                : 0, tab.invoiced),
            !!tab.paymentInformation.appliedPaymentsPrincipal
                ? tab.paymentInformation.appliedPaymentsPrincipal
                : tab.paymentInformation.tokenPaymentsTotal), tab.paymentInformation.surcharges), tab.donation);

        tab.markForLogging = (!!initialRemaining && multiplyCurrency(initialRemaining, 1.5) < tab.paymentInformation.remaining);
        return tab;
    }

    distributeTabCredits(tab: Tab) {
        const items: OrderItem[] = tab.orders.filter(o => o.status !== OrderStatuses.CANCELED)
            .reduce((all, o) => all.concat(o.items), [])
            .sort((a, b) => subtractCurrency(a.total, a.calcCredits()) - subtractCurrency(b.total, b.calcCredits()));

        items.forEach(i => i.tabCredits = 0);

        if (tab.paymentInformation.tabCredits > 0) {
            let usedCredits = 0;
            let itemsCount = items.length;
            let adjusted = divideCurrency(tab.paymentInformation.tabCredits, itemsCount);
            let itemTotalLessCredits: number;
            let oItem: OrderItem;
            let index = 0;

            while (itemsCount >= 1) {
                oItem = items[index++];
                if (itemsCount === 1) {
                    adjusted = subtractCurrency(tab.paymentInformation.tabCredits, usedCredits);
                }
                itemTotalLessCredits = subtractCurrency(oItem.total, oItem.calcCredits());

                if (adjusted < itemTotalLessCredits) {
                    oItem.tabCredits = adjusted;
                    usedCredits = addCurrency(usedCredits, adjusted);
                } else {
                    oItem.tabCredits = itemTotalLessCredits;
                    usedCredits = addCurrency(usedCredits, itemTotalLessCredits);
                }

                if (itemsCount > 1) {
                    adjusted = divideCurrency(subtractCurrency(tab.paymentInformation.tabCredits, usedCredits), itemsCount - 1);
                }

                itemsCount--;
            }
        }
    }

    groupTaxedAmounts(tab: Tab) {
        if (!tab.taxExemptionId) {
            let tabTaxes = tab.orders.filter(o => o.status !== OrderStatuses.CANCELED)
                .reduce((taxes: Tax[], o) => {
                    o.groupTaxedAmounts(o, tab);

                    o.items.forEach(item => {
                        if (!!item.taxes) {
                            item.taxes.forEach(itemTax => {
                                let tax = taxes.find(t => t.name === itemTax.name
                                    && t.taxRate === itemTax.taxRate
                                    && t.taxIncluded === itemTax.taxIncluded);

                                if (!tax) {
                                    tax = new Tax({...itemTax});
                                    tax.tax = null;
                                    taxes.push(tax);
                                } else {
                                    tax.taxedTotal = addCurrency(tax.taxedTotal, itemTax.taxedTotal);
                                }
                            });
                        }
                    });
                    return taxes;
                }, []);

            let addedTaxes = 0;
            tabTaxes.forEach(t => {
                t.tax = calculateTax(t.taxedTotal, t.taxRate);
                if (!t.taxIncluded) {
                    addedTaxes = addCurrency(addedTaxes, t.tax);
                }
            });
            tabTaxes = tabTaxes.filter(t => t.tax > 0);
            tab.paymentInformation.taxes = tabTaxes.reduce((total, tax) => addCurrency(total, tax.tax), 0);
            tab.taxes = tabTaxes;
            this.distributeTabTaxes(tab);
            tab.addedTaxes = addedTaxes;
            tab.paymentInformation.taxes = addedTaxes;
        } else {
            tab.taxes = [];
            tab.addedTaxes = 0;
            tab.paymentInformation.taxes = 0;
        }
    }

    distributeTabTaxes(tab: Tab) {
        if (!!tab.taxes && tab.taxes.length > 0 && !!tab.orders) {
            tab.taxes.forEach(t => {
                const items = tab.orders.reduce((filteredItems, o) =>
                    filteredItems.concat(o.items.filter(i => !!i.taxes && i.taxes.some(itemTax =>
                        t.name === itemTax.name
                        && t.taxRate === itemTax.taxRate
                        && t.taxIncluded === itemTax.taxIncluded))), []);

                const itemTaxTotal = items.reduce((total: number, i) => {
                    const found = i.taxes.find((itemTax: Tax) => t.name === itemTax.name
                        && t.taxRate === itemTax.taxRate
                        && t.taxIncluded === itemTax.taxIncluded);

                    return addCurrency(total, !!found ? found.tax : 0);
                }, 0);

                let remaining = subtractCurrency(t.tax, itemTaxTotal);
                let itemIndex = 0;
                while (remaining !== 0 && itemIndex < items.length) {
                    const item = items[itemIndex++];
                    const found = item.taxes.find((itemTax: Tax) => t.name === itemTax.name
                        && t.taxRate === itemTax.taxRate
                        && t.taxIncluded === itemTax.taxIncluded);

                    if (!!found) {
                        if (remaining > 0) {
                            found.tax = addCurrency(found.tax, .01);
                            remaining = subtractCurrency(remaining, .01);
                        } else {
                            found.tax = subtractCurrency(found.tax, .01);
                            remaining = addCurrency(remaining, .01);
                        }
                    }
                }
            });
        }
    }

    applyMembershipTokens(tab: Tab, activeMembership: Membership, modifiedTabComponents: ModifiedTabComponents) {
        if (!!tab
            && !!tab.orders
            && tab.orders.length > 0
            && !!this.organization.tokenGroups
            && this.organization.tokenGroups.length > 0) {

            let adjusted = 0;
            tab.orders.forEach(order => {
                order.items.forEach(orderItem => {
                    if (!!orderItem.tokenPayment && ((!!orderItem.credits && orderItem.credits.length > 0) || !activeMembership)) {
                        if (!!orderItem.tokenPayment) {
                            adjusted = adjusted = orderItem.tokenPayment;

                            const index = modifiedTabComponents.items.findIndex(i => i.key === orderItem.key
                                || (!!orderItem.id && orderItem.id === i.id));
                            if (index > -1) {
                                modifiedTabComponents.items[index] = orderItem;
                            } else {
                                modifiedTabComponents.items.push(orderItem);
                            }
                        }
                        orderItem.tokenPayment = null;
                        orderItem.membershipTokenGroupId = null;
                    }
                });
            });

            let availableTokens = (!!activeMembership ? activeMembership.availableTokens : 0) - adjusted;

            if (!!activeMembership) {
                for (const order of tab.orders) {
                    for (const orderItem of order.items) {
                        if ((!orderItem.credits || orderItem.credits.length === 0) && !!activeMembership) {
                            const tokenGroup = this.organization.tokenGroups
                                .find(g => g.items.some(i =>
                                    !!orderItem.selectedPrice && i.priceId === orderItem.selectedPrice.id));

                            const availability = !!activeMembership && !!activeMembership.availableTokenGroups && !!tokenGroup ?
                                activeMembership.availableTokenGroups.find(t => t.id === tokenGroup.id) : null;

                            if (!!availability
                                && !orderItem.id
                                && orderItem.tokenPayment === tokenGroup.tokenCount
                                && orderItem.membershipTokenGroupId === tokenGroup.id) {
                                availability.currentUsed++;
                            } else if (!!tokenGroup
                                && availableTokens >= tokenGroup.tokenCount
                                && !!availability
                                && (!availability.remaining || availability.remaining > availability.currentUsed)
                                && orderItem.tokenPayment !== tokenGroup.tokenCount
                                && !orderItem.preventAutoCredit
                            ) {
                                orderItem.tokenPayment = tokenGroup.tokenCount;
                                orderItem.membershipTokenGroupId = tokenGroup.id;
                                availableTokens -= orderItem.tokenPayment;
                                availability.currentUsed++;

                                const index = modifiedTabComponents.items.findIndex(i => i.key === orderItem.key
                                    || (!!orderItem.id && orderItem.id === i.id));
                                if (index > -1) {
                                    modifiedTabComponents.items[index] = orderItem;
                                } else {
                                    modifiedTabComponents.items.push(orderItem);
                                }
                            }
                        } else {
                            orderItem.tokenPayment = null;
                            orderItem.membershipTokenGroupId = null;
                        }
                    }
                }
            }
        }
    }

    applyAll(order: Order, t: Tab, activeEvent: OrganizationEvent, activeMembership: Membership): ModifiedTabComponents {
        const modifiedTabComponents = new ModifiedTabComponents({
            items: [],
            addedTabCredits: [],
            removedTabCredits: [],
            tab: null
        });

        if (!!t) {
            if (!t.orders) {
                t.orders = [];
            }
            t.orders = t.orders.filter(o => o.status !== OrderStatuses.PENDING);
            if (!!order) {
                t.orders.push(order);
            }
            let tab = this.recalculate(t);

            tab = this.applyAutoComps(tab, activeEvent, !!tab && !!tab.member && !!tab.member
                ? tab.member : activeMembership, modifiedTabComponents);
            this.applyMembershipTokens(tab, activeMembership, modifiedTabComponents);
            this.updateTokenAvailability(tab, activeMembership);
            modifiedTabComponents.tab = this.recalculate(tab);
        }
        return modifiedTabComponents;
    }

    applyComps(tab: Tab) {
        tab.paymentInformation.orderItemCredits = tab.orders.reduce((all, o) => all.concat(o.items), [])
            .reduce((c, i) => c.concat(i.credits), [])
            .reduce((total: number, ic) => addCurrency(total, ic.amount), 0);

        const directComps = tab.credits.filter(c => c.compDefinition.type !== 'PERCENT')
            .reduce((total, credit) => addCurrency(total, credit.amount), tab.paymentInformation.orderItemCredits);

        const tabSubtotalLessCompsAndTokens =
            subtractCurrency(
                subtractCurrency(tab.subtotal, directComps),
                tab.paymentInformation.tokenPaymentsTotal);

        tab.credits.filter(c => c.compDefinition.type === 'PERCENT')
            .forEach(c => c.amount = multiplyCurrency(tabSubtotalLessCompsAndTokens, divideCurrency(c.compDefinition.amount, 100)));

        tab.paymentInformation.tabCredits = tab.credits.reduce((total, credit) => addCurrency(total, credit.amount), 0);

        tab.paymentInformation.credits = addCurrency(tab.paymentInformation.tabCredits, tab.paymentInformation.orderItemCredits);

        this.distributeTabCredits(tab);
    }

    applyAutoComps(t: Tab, activeEvent: OrganizationEvent, activeMembership: Membership, modifiedTabComponents: ModifiedTabComponents): Tab {
        if (!!t) {
            let tab = this.applyTabComps(t, activeEvent, activeMembership, modifiedTabComponents);
            if (!tab) {
                this.applyOrderItemComps(t.orders, activeEvent, activeMembership, t.notes);
                tab = t;
            } else {
                tab.orders.forEach(o => {
                    o.items.forEach(orderItem => {
                        if (!!orderItem.credits && orderItem.credits.some(c => c.autoApplied)) {
                            orderItem.creditsModified = true;
                            orderItem.credits = orderItem.credits.filter(c => !c.autoApplied);
                        }
                    });
                });
            }

            modifiedTabComponents.items = !!tab && !!tab.orders
                ? tab.orders.filter(o => o.status !== OrderStatuses.CANCELED && o.status !== OrderStatuses.PENDING)
                    .reduce((acc, o) => acc.concat(o.items.filter(i => !!i.id && i.creditsModified)), [])
                : [];

            return tab;
        }
        return null;
    }

    updateTokenAvailability(tab: Tab, activeMembership: Membership) {
        if (!!activeMembership && !!activeMembership.availableTokenGroups && !!tab && !!tab.orders) {
            activeMembership.availableTokenGroups.forEach(t => t.currentUsed = 0);

            tab.orders.forEach(order => {
                order.items.forEach(i => {
                    if (!!i.membershipTokenGroupId) {
                        const availability = activeMembership.availableTokenGroups.find(t => t.id === i.membershipTokenGroupId);
                        if (!!availability && !!availability.remaining) {
                            availability.currentUsed++;
                        }
                    }
                });
            });
        }
    }

    applyOrderItemComps(orders: Order[], activeEvent: OrganizationEvent, activeMembership: Membership, tabNotes: TabNote[]) {
        if (!!orders && orders.length > 0) {
            orders.forEach(o => {
                if (!!o.items) {
                    o.items.forEach(i => {
                        if (!!i.credits) {
                            const count = i.credits.length;
                            i.credits = i.credits.filter(c =>
                                    !c.autoApplied
                                    || (
                                        ((!c.compDefinition.membershipPrograms || c.compDefinition.membershipPrograms.length === 0)
                                            || (!!activeMembership
                                                && !!activeMembership.activeMembershipProgram
                                                && c.compDefinition.membershipPrograms.some(p =>
                                                    p.id === activeMembership.activeMembershipProgram.id)
                                            )
                                        )
                                        && ((!c.compDefinition.organizationEvents || c.compDefinition.organizationEvents.length === 0)
                                            || (!!o.organizationEvent
                                                && c.compDefinition.organizationEvents.some(p => p.id === o.organizationEvent.id)
                                            )
                                        )
                                        && (!c.compDefinition.requiredTabNote
                                            || (!!tabNotes && tabNotes.some(n => n.definition.id === c.compDefinition.requiredTabNote.id)))
                                    )
                            );

                            if (i.credits.length !== count) {
                                i.creditsModified = true;
                            }
                        }
                    });
                }
            });
        }

        const allItems = !!orders && orders.length > 0 ?
            orders.filter(o => o.status !== OrderStatuses.CANCELED && (!o.id || o.status !== OrderStatuses.PENDING))
                .reduce((acc, order) =>
                    acc.concat(order.items.filter(i => !!i.menuItem && !!i.selectedPrice && !i.preventAutoCredit)), []) : [];

        const comps = this.getComps('ORDER_ITEM', activeEvent, activeMembership, tabNotes);
        comps.forEach(c => {
            const conditionItems = !!c.conditionItems && c.conditionItems.length > 0 ?
                allItems.filter(i => c.conditionItems.some(s => i.menuItem.id === s.itemId
                    && i.selectedPrice.id === s.priceId)) : null;
            const compItems = allItems.filter(i => !c.items
                || c.items.length === 0
                || c.items.some(s => i.menuItem.id === s.itemId
                    && i.selectedPrice.id === s.priceId));
            let sorted = [...compItems];
            if (c.priority === 'GREATEST') {
                sorted = sorted.sort((a, b) => (b.selectedPrice.price - a.selectedPrice.price === 0)
                    ? a.addIndex - b.addIndex : b.selectedPrice.price - a.selectedPrice.price);
            } else if (c.priority === 'LEAST') {
                sorted = sorted.sort((a, b) => (b.selectedPrice.price - a.selectedPrice.price === 0)
                    ? a.addIndex - b.addIndex : a.selectedPrice.price - b.selectedPrice.price);
            } else if (c.priority === 'LAST') {
                sorted = sorted.sort((a, b) => b.addIndex - a.addIndex);
            } else {
                sorted = sorted.sort((a, b) => a.addIndex - b.addIndex);
            }

            allItems.forEach(orderItem => {
                orderItem.tempCreditCount = orderItem.credits.length;
                orderItem.credits = orderItem.credits.filter(oic => oic.compDefinition.id !== c.id || !oic.autoApplied);
            });

            if (c.combinedCount > 0) {
                if (!!conditionItems && conditionItems.length > 0 && conditionItems.length >= c.requiredCount) {
                    let groups = Math.floor(conditionItems.length / c.combinedCount);

                    groups = !c.limitCount || groups < c.limitCount ? groups : c.limitCount;

                    // do comps in groups
                    for (let g = 0; g < groups; g++) {
                        const items = sorted.splice(0, c.combinedCount);
                        const itemsTotal = items.reduce((total: number, item: OrderItem) => total = addCurrency(total, item.total), 0);
                        if (itemsTotal > c.amount) {
                            let compAmount = subtractCurrency(itemsTotal, c.amount);
                            items.forEach(i => {
                                if (compAmount > 0) {
                                    if (compAmount <= i.total) {
                                        this.createOrderItemComp(i, c, compAmount);
                                        compAmount = 0;
                                    } else {
                                        this.createOrderItemComp(i, c, i.total);
                                        compAmount = subtractCurrency(compAmount, i.total);
                                    }
                                }
                            });
                        }
                    }
                }
            } else {
                const manualComped = allItems.filter(i => i.credits.some(oic => !oic.autoApplied && oic.compDefinition.id === c.id));
                let bogoLimit = null;
                let bogoGroupCount = null;
                let bogoGroups = null;
                let bogoOffset = null;
                if (!!c.requiredCount) {
                    if (!!conditionItems && conditionItems.length > 0 && conditionItems.length >= c.requiredCount) {
                        if (!c.rewardCount) {
                            // unlimited after requirement is met
                            const compItemsLessConditional = compItems.filter(i => !conditionItems.some(s => i.key === s.key));
                            bogoLimit = compItemsLessConditional.length;
                        } else {
                            // reward count can be greater than required to do looping (i.e. 2 bogo orders)
                            bogoGroupCount = c.requiredCount > c.rewardCount ? c.requiredCount : c.rewardCount;
                            bogoGroups = Math.floor(conditionItems.length / bogoGroupCount);

                            bogoLimit = bogoGroups * c.rewardCount;

                            const remainder = conditionItems.length % bogoGroupCount;
                            if (remainder >= c.requiredCount) {
                                bogoGroups++;
                                if (c.requiredCount > c.rewardCount) {
                                    bogoLimit += remainder - c.requiredCount;
                                } else {
                                    bogoLimit += remainder;
                                }
                            }

                            bogoOffset = c.requiredCount > c.rewardCount ? c.requiredCount - c.rewardCount : 0;
                        }
                    } else {
                        bogoLimit = 0;
                    }
                }

                let limit = (!!c.limitCount && c.limitCount <= compItems.length ? c.limitCount : compItems.length);

                if (c.requiredCount > 0 && bogoLimit < limit) {
                    limit = bogoLimit;
                }

                limit = limit - manualComped.length;

                if (!bogoLimit || c.priority === 'GREATEST' || c.priority === 'LEAST') {
                    for (let i = 0; i < limit; i++) {
                        this.createOrderItemComp(sorted[i], c);
                    }
                } else {
                    // do comps in groups
                    let sortedIndex = 0;
                    let rewarded = 0;
                    for (let g = 0; g < bogoGroups; g++) {
                        if (bogoOffset > 0) {
                            sortedIndex += bogoOffset;
                        }

                        for (let s = 0; s < c.rewardCount && rewarded <= limit && sorted.length > sortedIndex; s++) {
                            this.createOrderItemComp(sorted[sortedIndex++], c);
                            rewarded++;
                        }
                    }
                }
            }

            allItems.forEach(orderItem =>
                orderItem.creditsModified = orderItem.creditsModified || orderItem.credits.length !== orderItem.tempCreditCount);
        });
    }

    applyTabComps(
        t: Tab,
        activeEvent: OrganizationEvent,
        activeMembership: Membership,
        modifiedTabComponents: ModifiedTabComponents
    ): Tab {
        if (!!t && !!t.credits) {
            modifiedTabComponents.removedTabCredits = t.credits.filter(c => c.autoApplied);
            t.credits = t.credits.filter(c => !c.autoApplied);
        }

        const tab = this.recalculate(t);

        if (tab.paymentInformation.remaining > 0 && !tab.preventAutoCredit && tab.tipAgainstAmount > 0) {
            const tabComps = this.getComps('TAB', activeEvent, activeMembership, tab.notes);

            if (!!tab && (!tab.credits || tab.credits.length === 0) && !!tabComps && tabComps.length > 0) {
                if (tabComps.length > 1) {
                    tabComps.sort((a, b) => b.amount - a.amount);
                }

                const tabCredit = new TabCredit({
                    parentId: tab.id,
                    addIndex: 0,
                    compDefinition: tabComps[0],
                    autoApplied: true
                });

                if (!tab.credits) {
                    tab.credits = [];
                } else if (tab.credits.length > 1) {
                    return;
                } else if (tab.credits.length === 1) {
                    if (tab.credits[0].amount > tabCredit.compDefinition.amount) {
                        return;
                    } else {
                        tab.credits = [];
                    }
                }

                tab.credits.push(tabCredit);
                modifiedTabComponents.addedTabCredits.push(tabCredit);
                return this.recalculate(tab);
            }
        }
        return null;
    }

    getComps(type: string, activeEvent: OrganizationEvent, activeMembership: Membership, tabNotes: TabNote[]) {
        return this.autoComps.filter(c => c.availability === type
            && (((!c.membershipPrograms || c.membershipPrograms.length === 0)
                    || (!!activeMembership
                        && !!activeMembership.activeMembershipProgram
                        && c.membershipPrograms.some(p => p.id === activeMembership.activeMembershipProgram.id)
                        && !!activeMembership.availableComps
                        && activeMembership.availableComps.some(ac => ac.id === c.id)
                    )
                )
                && ((!c.organizationEvents || c.organizationEvents.length === 0)
                    || (!!activeEvent
                        && c.organizationEvents.some(p => p.id === activeEvent.id)
                    )
                )
                && (!c.requiredTabNote
                    || (!!tabNotes && tabNotes.some(n => n.definition.id === c.requiredTabNote.id)))
                && (c.availability !== 'ORDER_ITEM' || !c.requiredCount
                    || (c.requiredCount > 0 && !!c.conditionItems && c.conditionItems.length > 0)))
        ).sort((a, b) => {
            if (a.limitDurationPart === b.limitDurationPart) {
                return b.limitDurationCount - a.limitDurationCount;
            } else {
                return this.durationRank(a.limitDurationPart) - this.durationRank(b.limitDurationPart);
            }
        });
    }

    createOrderItemComp(orderItem: OrderItem, compDefinition: CompDefinition, amount = null) {
        const credit = new OrderItemCredit({compDefinition});
        credit.addIndex = !!orderItem.credits ? orderItem.credits.reduce((max, c) => max <= c.addIndex ? c.addIndex + 1 : max, 1) : 1;
        credit.autoApplied = true;

        if (!!amount) {
            credit.amount = amount;
        } else if (compDefinition.type === CompDefinitionTypes.DEFINED) {
            credit.amount = compDefinition.amount;
        } else if (compDefinition.type === CompDefinitionTypes.PERCENT) {
            credit.amount = multiplyCurrency(orderItem.calcTotal(), divideCurrency(compDefinition.amount, 100));
        }

        if (!!orderItem.credits) {
            orderItem.creditsTotal = orderItem.credits.reduce((total, c: OrderItemCredit) => total = addCurrency(total, c.amount), 0);
            const remaining = subtractCurrency(orderItem.calcTotal(), orderItem.creditsTotal);
            if (credit.amount > remaining) {
                if (compDefinition.amount === 100 && compDefinition.type === CompDefinitionTypes.PERCENT) {
                    credit.amount = orderItem.calcTotal();
                    orderItem.credits = [];
                } else {
                    credit.amount = remaining;
                }
            }
        }
        if (credit.amount > 0) {
            if (!orderItem.credits) {
                orderItem.credits = [];
            }
            orderItem.tokenPayment = null;
            orderItem.membershipTokenGroupId = null;
            orderItem.creditsModified = true;
            orderItem.credits.push(credit);
        }
    }

    adjustForPaymentMethod(t: Tab, paymentMethod: string, amount: number, tip: number): Tab {
        if (!!t) {
            const tab = new Tab(t);
            const paymentType = !tab.paymentInformation.paymentType
            || tab.paymentInformation.paymentType === paymentMethod
            || tab.paymentInformation.paymentType === 'NONE'
                ? paymentMethod
                : 'MIX';

            let d = tab.paymentInformation.remaining;

            const taxes = [];
            if (!!tab.taxes && !t.taxExemptionId) {
                tab.orders.forEach(o => {
                    o.items.forEach(oi => {
                        if (!!oi.taxes && oi.taxes.length > 0) {
                            oi.taxes.filter(t => !t.taxIncluded).forEach(tax => {
                                if (((!!oi.menuItem && !oi.menuItem.taxOverride) || !!oi.membershipRenewal)
                                    && ((paymentType === 'CASH' && tax.taxIncludedCash)
                                        || (paymentType === 'CHARGE' && tax.taxIncludedCharge)
                                        || (paymentType === 'GIFT_CARD' && tax.taxIncludedGiftCard))) {
                                    d = subtractCurrency(d, tax.tax);
                                } else {
                                    const found = taxes.find(tabTax => tabTax.name === tax.name
                                        && tabTax.taxRate === tax.taxRate
                                        && tabTax.taxIncluded === tax.taxIncluded);
                                    if (!found) {
                                        taxes.push(new Tax(tax));
                                    } else {
                                        found.tax = addCurrency(tax.tax, found.tax);
                                    }
                                }
                            });
                        }
                    });
                });
            }

            tab.taxes = taxes;
            tab.due = d;
            tab.addedTaxes = tab.taxes.filter(t => !t.taxIncluded)
                .reduce((total: number, t: Tax) => total = addCurrency(total, t.tax), 0);

            tab.surcharge = this.calculateSurcharge(tab, paymentMethod, (!!amount || amount === 0) ? amount : tab.due, tip);
            return tab;
        }
        return null;
    }

    calculateSurcharge(tab: Tab, paymentMethod: string, amount: number, tip: number) {
        const total = addCurrency(tip, amount);

        if (((!!tab.cardOnFile && !paymentMethod) || paymentMethod === 'CHARGE' || paymentMethod === 'CLOSE_TAB')
            && !!this.organization
            && !!this.organization.organizationPreference
            && this.organization.organizationPreference.surcharge > 0) {
            return multiplyCurrency(this.organization.organizationPreference.surchargeIncludeTip ? total : amount,
                convertPercentage(this.organization.organizationPreference.surcharge));
        } else {
            return 0;
        }
    }

    durationRank(limitDurationPart: string): number {
        switch (limitDurationPart) {
        case 'EVER':
            return 1;
        case 'YEAR':
            return 2;
        case 'MONTH':
            return 3;
        case 'WEEK':
            return 4;
        case 'DAY':
            return 5;
        case 'TAB':
            return 6;
        }
    }

}
